/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    const initialVariant = this.getSelectedVariant()
    this.adjustPricing(initialVariant)
    this.adjustButtons(initialVariant)
  }

  getSelectedVariant () {
    const variantId = Number(this.form.querySelector('[name="id"]')?.value)
    if (!variantId || !this.form.dataset.variants) return null

    try {
      const variants = JSON.parse(this.form.dataset.variants)
      return variants.find(v => v.id === variantId) || null
    } catch (e) {
      console.error(e)
      return null
    }
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptions = []

    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })

    // console.log(selectedOptions)

    const variants = JSON.parse(this.form.dataset.variants || '[]')
    const selectedVariant = variants.find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    ) || null

    // console.log(selectedVariant)

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant) 

    if (!selectedVariant) return

    const idInput = this.querySelector('[name="id"]')
    if (idInput) idInput.value = selectedVariant.id

    const url = new URL(window.location.href)
    url.searchParams.set('variant', String(selectedVariant.id))
    window.history.replaceState({}, '', url)

    window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
      detail: selectedVariant
    }))
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const saleBadges = this.closest('.product-main')?.querySelectorAll(
      '.product-price .product-sale-badge, .product-media-gallery .product-sale-badge'
    ) || []
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (!comparePrice || !finalPrice) return

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}</span>${window.Shopify.formatMoney(selectedVariant.price)} <small class="product-price-ttc">TTC</small>`
      finalPrice.classList.add('product-price-final-sale')
      saleBadges.forEach(badge => badge.removeAttribute('hidden'))
      if (saleBadges[0]?.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        saleBadges.forEach(badge => { badge.textContent = `${window.theme.locales.product.save} ${savings}` })
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        saleBadges.forEach(badge => { badge.textContent = `${window.theme.locales.product.save} ${savings}` })
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = `${window.Shopify.formatMoney(selectedVariant.price)} <small class="product-price-ttc">TTC</small>`
      finalPrice.classList.remove('product-price-final-sale')
      saleBadges.forEach(badge => {
        badge.textContent = ''
        badge.setAttribute('hidden', 'hidden')
      })
    }

    if (soldOutBadge) {
      if (selectedVariant.available) {
        soldOutBadge.setAttribute('hidden', 'hidden')
      } else {
        soldOutBadge.removeAttribute('hidden')
      }
    }
  }

  adjustButtons (selectedVariant) {
    const qtyWrapper = this.form.querySelector('.qty-input-wrapper')
    const dynamicButtons = this.form.querySelector('.product-dynamic-buttons')

    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn?.removeAttribute('disabled')
        qtyWrapper?.removeAttribute('hidden')
        dynamicButtons?.removeAttribute('hidden')

        this.buyBtn?.removeAttribute('disabled')

        if (this.atcBtn) {
          this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
        }
      } else {
        this.atcBtn?.setAttribute('disabled', 'disabled')
        qtyWrapper?.setAttribute('hidden', 'hidden')
        dynamicButtons?.setAttribute('hidden', 'hidden')

        this.buyBtn?.setAttribute('disabled', 'disabled')

        if (this.atcBtn) {
          this.atcBtn.innerHTML = window.theme.locales.product.sold_out
        }
      }
    } else {
      this.atcBtn?.setAttribute('disabled', 'disabled')
      qtyWrapper?.setAttribute('hidden', 'hidden')
      dynamicButtons?.setAttribute('hidden', 'hidden')

      this.buyBtn?.setAttribute('disabled', 'disabled')

      if (this.atcBtn) {
        this.atcBtn.innerHTML = window.theme.locales.product.unavailable
      }
    }
  }

  async onSubmit (event) {
    event.preventDefault()

    if (!this.atcBtn) return

    const selectedVariant = this.getSelectedVariant()
    if (!selectedVariant || !selectedVariant.available || this.atcBtn?.disabled) {
      this.adjustButtons(selectedVariant)
      return
    }

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    const formData = new FormData(this.form)

    try {
      await document.querySelector('cart-container').add(formData)
    } finally {
      this.atcBtn.classList.remove('loading')
      this.atcBtn.disabled = false
      this.atcBtn.setAttribute('aria-busy', 'false')
      this.adjustButtons(this.getSelectedVariant())
    }

  }

  onClickBuyBtn () {
    const selectedVariant = this.getSelectedVariant()
    if (!selectedVariant || !selectedVariant.available || this.buyBtn?.disabled) return

    this.buyBtn.classList.add('loading')
    const variantId = this.form.querySelector('[name="id"]').value
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)
    window.location.href = `/cart/${variantId}:${qty}`
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)
    this.mainWrapper = this.querySelector('.swiper-main-wrapper')

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.initMediaPlaybackState()
    this.setStickyPosition()
    this.listenVariantChange()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }
    return options
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top') || document.querySelector('#header-group')

      const setTopOffset = () => {
        const headerHeight = headerGroup?.getBoundingClientRect().height || 0
        const headerPosition = headerGroup ? window.getComputedStyle(headerGroup).position : ''
        const topOffset = headerPosition === 'fixed' ? headerHeight : 0
        this.style.setProperty('--product-media-sticky-top', `${Math.round(topOffset)}px`)
      }

      setTopOffset()

      const onResize = () => setTopOffset()
      window.addEventListener('resize', onResize)

      if ('ResizeObserver' in window && headerGroup) {
        const ro = new ResizeObserver(() => setTopOffset())
        ro.observe(headerGroup)
      }
    }
  }

  initMediaPlaybackState () {
    if (!this.mainWrapper || !this.main) return

    const videos = this.querySelectorAll('.swiper-main video')

    const syncMediaPlaybackState = () => {
      const activeSlide = this.querySelector('.swiper-main .swiper-slide-active')

      if (!activeSlide) {
        this.setMediaPlaybackState(false)
        return
      }

      if (activeSlide.dataset.mediaType === 'external_video') {
        this.setMediaPlaybackState(true)
        return
      }

      const activeVideo = activeSlide.querySelector('video')
      const isPlaying = Boolean(activeVideo && !activeVideo.paused && !activeVideo.ended)
      this.setMediaPlaybackState(isPlaying)
    }

    videos.forEach(video => {
      video.addEventListener('play', syncMediaPlaybackState)
      video.addEventListener('pause', syncMediaPlaybackState)
      video.addEventListener('ended', syncMediaPlaybackState)
    })

    this.main.on('slideChangeTransitionStart', () => {
      this.setMediaPlaybackState(false)
    })

    this.main.on('slideChangeTransitionEnd', syncMediaPlaybackState)
    syncMediaPlaybackState()
  }

  setMediaPlaybackState (isPlaying) {
    this.mainWrapper.classList.toggle('is-media-playing', isPlaying)
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.main.slideTo(selectedVariant.featured_media.position - 1)
        this.setMediaPlaybackState(false)
      }
    }, false)
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
